/**
 * Which design a skeleton config holds. Mirrors the `list` and `table` members of `ViewType` by
 * value, so a widget's own view type can be handed straight to `resolveSkeletonViewType`; a view
 * with no design of its own — a dropdown, say — is designed as a list.
 *
 * It is deliberately its own enum rather than a re-export: `common.type` reaches back to this
 * module through `workflow.v2.type`, so importing `ViewType` here would close a require cycle.
 */
export enum SkeletonViewType {
  LIST = 'list',
  TABLE = 'table',
}

export enum SkeletonBlockShape {
  LINE = 'line',
  CIRCLE = 'circle',
}

/**
 * Where a block sits on its row's cross axis. A row lays its blocks out horizontally, so this is
 * vertical placement against the tallest block in the row — an avatar centred against the lines
 * beside it, or an amount pinned to the top of the row.
 */
export enum SkeletonBlockAlignment {
  START = 'flex-start',
  CENTER = 'center',
  END = 'flex-end',
}

/**
 * Which part of the row a block belongs to. Blocks run in the order they are designed, and each
 * group takes the space between it and the next: two blocks on the left and one on the right sit
 * at the row's two edges, whatever width the widget ends up. Consecutive blocks with the same
 * placement stay together as one group.
 */
export enum SkeletonBlockPlacement {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export enum SkeletonSizeUnit {
  PERCENT = '%',
  EM = 'em',
  REM = 'rem',
  PX = 'px',
  VH = 'vh',
  VW = 'vw',
}

/** Dropdown order, matching the unit lists used elsewhere in the app builder. */
export const SKELETON_SIZE_UNITS: SkeletonSizeUnit[] = [
  SkeletonSizeUnit.PERCENT,
  SkeletonSizeUnit.EM,
  SkeletonSizeUnit.REM,
  SkeletonSizeUnit.PX,
  SkeletonSizeUnit.VH,
  SkeletonSizeUnit.VW,
];

export type SkeletonBlockConfig = {
  shape: SkeletonBlockShape;
  /** ignored for CIRCLE, which is always as wide as it is tall */
  width: number;
  widthUnit: SkeletonSizeUnit;
  /** the diameter for CIRCLE */
  height: number;
  heightUnit: SkeletonSizeUnit;
  /** vertical placement in the row; a table cell fills its own box, so it has none */
  verticalAlign: SkeletonBlockAlignment;
  /** which part of the row the block belongs to; a table cell has none */
  placement: SkeletonBlockPlacement;
};

export type SkeletonRowConfig = {
  /** px; between blocks */
  gap: number;
  /** px; indent, so a row can line up past an avatar on the row above */
  offsetLeft: number;
  /** px; space above the row */
  marginTop: number;
  blocks: SkeletonBlockConfig[];
};

type SkeletonLayoutConfigBase = {
  /** when false the skeleton is not applied, and the widget's own loading state is used */
  enabled: boolean;
  randomiseWidth: boolean;
  randomiseHeight: boolean;
};

/** A list is designed row by row: rows of blocks, repeated down the list. */
export type SkeletonListLayoutConfig = SkeletonLayoutConfigBase & {
  viewType: SkeletonViewType.LIST;
  /** how many times the designed item is repeated */
  repeatCount: number;
  /** px; between repeated items */
  itemGap: number;
  rows: SkeletonRowConfig[];
};

/**
 * A table's structure is not designed at all — its rows come from the page size and its columns
 * from the columns the widget already shows — so a table design is just the block that fills a
 * cell, plus whether its size varies from cell to cell.
 */
export type SkeletonTableLayoutConfig = SkeletonLayoutConfigBase & {
  viewType: SkeletonViewType.TABLE;
  /** the block rendered inside every skeleton cell */
  cell: SkeletonBlockConfig;
};

/**
 * A widget holds the design for one view: switching the widget between list and table replaces
 * the saved design with one shaped for the new view, rather than carrying the other view's keys
 * around. A config saved before table designs existed carries no view type at all and reads as a
 * list design, exactly as it behaved.
 */
export type SkeletonLayoutConfig = SkeletonListLayoutConfig | SkeletonTableLayoutConfig;

/**
 * Which way the repeated items run. This is the widget's own layout setting rather than something
 * the builder designs on the skeleton — a widget whose items scroll sideways already knows it, and
 * a second copy on the design could only ever disagree with it.
 */
export enum SkeletonOrientation {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

/**
 * What the widget contributes to the markup, as opposed to what the builder designed. Everything
 * here is optional and defaults to the vertical full-width list, so a widget that has no
 * orientation to speak of passes nothing.
 */
export type SkeletonLayoutRenderOptions = {
  orientation?: string;
  /** the width one repeat takes when they run horizontally — the widget's own item width */
  itemWidth?: number | string;
  itemWidthUnit?: string;
};

/** Matches the carousel's own default item width, used when a widget's own value is unreadable. */
export const SKELETON_FALLBACK_ITEM_WIDTH = 93;

/** Widget attribute path the designed layout is saved on. */
export const SKELETON_LAYOUT_CONFIG_PATH = 'skeletonLayoutConfig';

/**
 * Widget attribute path holding the widget's own loading template — the fallback rendered
 * whenever no skeleton is applied.
 */
export const SKELETON_LAYOUT_FALLBACK_TEMPLATE_PATH = 'fetchingTemplate';

/**
 * Marker classes on generated markup. They let us tell a designed loading template apart
 * from a hand-written one, so we never clobber markup the app builder typed themselves.
 */
export const SKELETON_LAYOUT_ROOT_CLASS = 'skeleton-layout-template';
export const SKELETON_LAYOUT_ITEM_CLASS = 'skeleton-layout-item';
export const SKELETON_LAYOUT_ROW_CLASS = 'skeleton-layout-row';
export const SKELETON_LAYOUT_CELL_CLASS = 'skeleton-layout-cell';

/**
 * The inline size a table skeleton cell resolves to, ready to bind straight onto a block. Size is
 * all a cell carries: its shade and its corner come from global styling, through `.skeleton_item`.
 */
export type SkeletonCell = {
  width: string;
  height: string;
};

/** Rows a table skeleton shows when the widget has no page size to derive them from. */
export const SKELETON_TABLE_FALLBACK_ROWS = 5;
export const SKELETON_TABLE_ROW_LIMITS = { min: 1, max: 24 } as const;
export const SKELETON_TABLE_COLUMN_LIMITS = { min: 1, max: 12 } as const;

// Sizes carry a unit, so the floors stay small enough for em/rem values to survive clamping.
// Spacing floors at 0: a row with no indent and no space above it is a valid design, and a floor
// of 1 would quietly shift every saved design by a pixel.
export const SKELETON_REPEAT_COUNT_LIMITS = { min: 1, max: 20 } as const;
export const SKELETON_BLOCK_HEIGHT_LIMITS = { min: 1, max: 400 } as const;
export const SKELETON_BLOCK_WIDTH_LIMITS = { min: 1, max: 2000 } as const;
export const SKELETON_SPACING_LIMITS = { min: 0, max: 200 } as const;
